/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : AuthProvider.tsx
 *
 * Project Title : APM Settings Tool
 *
 * Description   : AuthProvider component
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : H422335
 *
 * @Date         : December 16, 2021
 *
 * @Version      : Version 1.0
 *
 *
 ******************************************************************************/

import React, { FC, createContext, useContext } from "react";
import { Search } from "history";
import { useDispatch, useSelector } from "react-redux";
import {
  IGetAuthTokenRequestArgs,
  EnumTokenType,
  ITokenState,
} from "models/auth";
import {
  appConfig,
  getLoginURL,
  getCodeChallenge,
  getLogoutURL,
} from "shared/utils";
import { fetchToken, selectIsAuthenticated } from "stores/AuthStore";

interface IAuthContextProps {
  isAuthenticated: boolean;
  login: (location: { search: Search }) => void;
  logout: (authtoken: ITokenState | null) => void;
}

const AuthContext = createContext<Partial<IAuthContextProps>>({});

export const AuthProvider: FC = ({ children }) => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const login = (location: { search: Search }) => {
    const query = new URLSearchParams(location.search);
    const code: string | null = query.get("code");

    if (!code) {
      const stateChallenge = getCodeChallenge();
      const loginURL = getLoginURL(stateChallenge, appConfig);
      window.location.href = loginURL;
    } else {
      dispatch(
        fetchToken<IGetAuthTokenRequestArgs>({
          type: EnumTokenType.Auth,
          code,
          appConfig,
        })
      );
    }
  };

  const logout = (authtoken: ITokenState | null) => {
    if (authtoken) {
      const stateChallenge = getCodeChallenge();
      const logoutURL = getLogoutURL(
        stateChallenge,
        appConfig,
        authtoken.id_token
      );

      window.location.href = logoutURL;
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): Partial<IAuthContextProps> =>
  useContext(AuthContext);
